import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingV2 from 'components/LoadingV2'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBPagination from 'components/PMBPagination'
import ProfilePic from 'components/PMBProfilePic'
import PMBProgressBarExportFile from 'components/PMBProgressBarExportFile'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import * as FileSaver from 'file-saver'

import PMBSelect from 'components/PMBSelect'
import { capitalizeEveryWord } from 'helpers'
import moment from 'moment-timezone'
import React, { useEffect, useState, useRef } from 'react'
import {
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  FileText,
  Filter,
  Info,
  Package,
  Search,
  X,
} from 'react-feather'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import {
  Badge,
  Card,
  CardBody,
  Col,
  Collapse,
  Row,
  Spinner,
  InputGroup,
  InputGroupText,
  Input,
} from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import * as XLSX from 'xlsx-js-style'
import { styleTableSheet } from 'utils/excelStyle'
import './detail.css'
import { handleDownloadExcelWithRTK } from './downloadWarehouse'
import {
  useFetchWarehouseByIdQuery,
  useFetchWarehouseSummaryQuery,
  useLazyFetchWarehouseSummaryQuery,
  useLazyFetchWarehouseSummaryV2Query,
  useFetchWarehouseItemsQuery,
} from './warehouseDetailApiSlice'
import { PMBDateRangePicker } from 'components/PMBNewDatePicker'

const DetailWarehouse = () => {
  const { id } = useParams()
  const intl = useIntl()

  const MIN_MAX_HEIGHT = '100px'
  const fetchTimeoutRef = useRef(0)
  const [activeTab, setActiveTab] = useState('monthly')
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedDate, setSelectedDate] = useState(() => {
    const start = moment().startOf('month').format('YYYY-MM-DD')
    const end = moment().endOf('month').format('YYYY-MM-DD')
    return [start, end]
  })
  const [sortColumn, setSortColumn] = useState('nameItem')
  const [sortDirection, setSortDirection] = useState('desc')
  const [imageItems, setImagesItems] = useState([])
  const [modalOpenImage, setModalOpenImage] = useState(false)
  const [search, setSearch] = useState('')
  const [searchTemp, setSearchTemp] = useState('')
  const [itemsFilter, setItemsFilter] = useState([])

  const [isDownload, setIsDownload] = useState(false)
  const [firstRows, setFirstRows] = useState(0)
  const [totalRows, setTotalRows] = useState(0)
  const [monthlyData, setMonthlyData] = useState([])
  const [monthlyLoading, setMonthlyLoading] = useState(false)

  // FETCH API BY WAREHOUSE DETAIL

  const formattedMonthLabel =
    selectedDate?.length === 2 && selectedDate[0] && selectedDate[1]
      ? `${moment(selectedDate[0]).format('DD MMM YYYY')} - ${moment(selectedDate[1]).format('DD MMM YYYY')}`
      : moment().format('MMMM YYYY')
  const { data: dataById, isLoading: isLoadingById } =
    useFetchWarehouseByIdQuery({
      id: id,
    })

  const dataWarehouse = dataById?.items?.[0]

  const fromDate =
    selectedDate?.length === 2 && selectedDate[0]
      ? moment(selectedDate[0]).format('YYYY-MM-DD')
      : null

  const toDate =
    selectedDate?.length === 2 && selectedDate[1]
      ? moment(selectedDate[1]).format('YYYY-MM-DD')
      : null

  const { data: itemsData } = useFetchWarehouseItemsQuery(
    {
      page: 0,
      size: 999,
      warehouseId: id ?? null,
      startdate: selectedDate?.[0]
        ? moment(selectedDate[0]).toISOString()
        : null,
      enddate: selectedDate?.[1] ? moment(selectedDate[1]).toISOString() : null,
    },
    { skip: !fromDate || !toDate },
  )

  const { data, isLoading, isFetching } = useFetchWarehouseSummaryQuery(
    {
      page: currentPage ?? 0,
      size: rowsPerPage ?? 10,
      sortby: sortColumn,
      sortdir: sortDirection,
      warehouseId: id ?? null,
      from: fromDate,
      to: toDate,
      type: activeTab === 'all' ? 'current' : activeTab,
      s: searchTemp,
      itemIds:
        itemsFilter?.length > 0
          ? `[${itemsFilter.map((i) => i.value).join(',')}]`
          : undefined,
    },
    { skip: activeTab === 'monthly' || !fromDate || !toDate },
  )

  // LAZY QUERY FOR DOWNLOAD
  const [triggerFetchForDownload] = useLazyFetchWarehouseSummaryQuery()
  const [triggerFetchBalanceV2] = useLazyFetchWarehouseSummaryV2Query()

  // Fetch combined monthly report (Balance M-1, Incoming M, Outgoing M, Balance M)
  const fetchMonthlyReport = async (selectedDate) => {
    if (!id) return
    if (
      !selectedDate ||
      selectedDate.length < 2 ||
      !selectedDate[0] ||
      !selectedDate[1]
    )
      return
    setMonthlyLoading(true)
    try {
      const startDate = moment(selectedDate[0]).format('YYYY-MM-DD')
      const endDate = moment(selectedDate[1]).format('YYYY-MM-DD')

      const paramsBase = (type) => ({
        warehouseId: id ?? null,
        from: startDate,
        to: endDate,
        type,
        page: 0,
        size: 9999,
        s: searchTemp,
        itemIds:
          itemsFilter?.length > 0
            ? `[${itemsFilter.map((i) => i.value).join(',')}]`
            : undefined,
      })

      // Balance column should reflect the day before the selected range start
      const datev2 = selectedDate?.[1]
        ? moment(selectedDate[1]).toISOString()
        : null

      const [resBal, resIn, resOut] = await Promise.all([
        triggerFetchBalanceV2({
          warehouseId: id ?? null,
          type: 'balance',
          date: datev2,
          page: 0,
          size: 9999,
          s: searchTemp,
          itemIds:
            itemsFilter?.length > 0
              ? `[${itemsFilter.map((i) => i.value).join(',')}]`
              : undefined,
        }),
        triggerFetchForDownload(paramsBase('in')),
        triggerFetchForDownload(paramsBase('out')),
      ])

      const balItems = resBal?.data?.items || []
      const inItems = resIn?.data?.items || []
      const outItems = resOut?.data?.items || []

      const map = new Map()

      const addToMap = (arr, field) => {
        arr.forEach((r) => {
          const nameRaw = r?.nameItem || r?.item?.nameItem || '-'
          const unitRaw = r?.unit || r?.item?.unit || ''
          // Normalize: trim + lowercase for key grouping, preserve display as capitalized
          const name = nameRaw.trim()
          const unitNormalized = unitRaw.trim().toLowerCase()
          const displayUnit = unitNormalized // choose a single standard form
          const key = `${name}-${unitNormalized}`

          if (!map.has(key)) {
            map.set(key, {
              nameItem: name,
              unit: capitalizeEveryWord(displayUnit),
            })
          }
          const entry = map.get(key)
          entry[field] = Number(r?.total ?? r?.initialAmount ?? 0)
          map.set(key, entry)
        })
      }

      addToMap(balItems, 'balanceM')
      addToMap(inItems, 'incoming')
      addToMap(outItems, 'outgoing')

      const rows = Array.from(map.entries()).map(([k, v]) => {
        const incoming = Number(v.incoming || 0)
        const outgoing = Number(v.outgoing || 0)

        // API mengembalikan Balance Akhir
        const balanceM = Number(v.balanceM || 0)
        const balanceMMinus1 = balanceM - incoming + outgoing

        return {
          key: k,
          nameItem: v.nameItem || '-',
          unit: v.unit || '',
          balanceMMinus1,
          incoming,
          outgoing,
          balanceM,
        }
      })

      setMonthlyData(rows)
      setCurrentPage(0)
    } catch (e) {
      console.error('Failed to fetch monthly report', e)
      setMonthlyData([])
    } finally {
      setMonthlyLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'monthly') {
      fetchMonthlyReport(selectedDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedDate, id, searchTemp, itemsFilter])

  // Summary values for monthlyData
  const monthlySummary = React.useMemo(() => {
    if (!monthlyData || monthlyData.length === 0)
      return { totalItems: 0, totalsByUnit: {} }
    const totalItems = monthlyData.length
    const totalsByUnit = {}
    monthlyData.forEach((r) => {
      const unit = (r?.unit || 'pcs').toLowerCase()
      if (!totalsByUnit[unit])
        totalsByUnit[unit] = { incoming: 0, outgoing: 0, balanceEnd: 0 }
      totalsByUnit[unit].incoming += Number(r.incoming || 0)
      totalsByUnit[unit].outgoing += Number(r.outgoing || 0)
      totalsByUnit[unit].balanceEnd += Number(r.balanceM || 0)
    })
    return { totalItems, totalsByUnit }
  }, [monthlyData])

  const paginatedMonthlyData = React.useMemo(() => {
    const start = currentPage * rowsPerPage
    const end = start + rowsPerPage
    return monthlyData.slice(start, end)
  }, [monthlyData, currentPage, rowsPerPage])

  const dataResponse = Array.isArray(data?.items) ? data?.items : []

  const handleDateChange = (selectedDates) => {
    if (
      Array.isArray(selectedDates) &&
      selectedDates.length === 2 &&
      selectedDates[0] &&
      selectedDates[1]
    ) {
      setSelectedDate(selectedDates)
      setCurrentPage(0)
    }
  }

  const handleDateClear = () => {
    const start = moment().startOf('month').format('YYYY-MM-DD')
    const end = moment().endOf('month').format('YYYY-MM-DD')
    setSelectedDate([start, end])
    setCurrentPage(0)
  }

  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const activeFilterCount = React.useMemo(() => {
    let count = 0
    if (searchTemp?.trim().length > 0) count++
    if (itemsFilter?.length > 0) count++
    return count
  }, [searchTemp, itemsFilter])

  const handleClearFilters = () => {
    setSearch('')
    setSearchTemp('')
    setItemsFilter([])
    handleDateClear()
    setCurrentPage(0)
  }

  const handleSearch = (e) => {
    setSearch(e)
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current)
    }

    fetchTimeoutRef.current = setTimeout(() => {
      if (e.trim() !== '') {
        setSearchTemp(e)
        setCurrentPage(0)
      } else {
        setSearch('')
        setSearchTemp('')
        setCurrentPage(0)
      }
    }, 1000)
  }

  const handleRemoveItemFilter = (itemValue) => {
    setItemsFilter((prev) => prev.filter((i) => i.value !== itemValue))
    setCurrentPage(0)
  }

  // HANDLE SORTING
  const handleSort = (column, direction) => {
    setSortColumn(column.selector)
    setSortDirection(direction === 'asc' ? 'asc' : 'desc')
  }

  // HANDLE DOWNLOAD WITH DYNAMIC PAGINATION LOOP
  const handleDownloadClick = async () => {
    if (activeTab === 'monthly') {
      // Export the computed monthly data
      try {
        const prevDateLabel = selectedDate?.[0]
          ? moment(selectedDate[0]).subtract(1, 'day').format('DD MMM YYYY')
          : '-'
        const wb = XLSX.utils.book_new()
        const header = [
          'Jenis Barang',
          `Balance (s.d. ${prevDateLabel})`,
          `Barang Masuk (${formattedMonthLabel})`,
          `Barang Keluar (${formattedMonthLabel})`,
          `Balance Akhir (${formattedMonthLabel})`,
          'Satuan',
        ]
        const ws = XLSX.utils.aoa_to_sheet([header])
        const rows = (monthlyData || []).map((r) => [
          r.nameItem || '-',
          r.balanceMMinus1 ?? 0,
          r.incoming ?? 0,
          r.outgoing ?? 0,
          r.balanceM ?? 0,
          r.unit || 'pcs',
        ])
        XLSX.utils.sheet_add_aoa(ws, rows, { origin: 'A2' })
        XLSX.utils.book_append_sheet(wb, styleTableSheet(ws), 'Laporan Bulanan')
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const finalData = new Blob([excelBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
        })
        FileSaver.saveAs(
          finalData,
          `Laporan_Bulanan_${dataWarehouse?.name ?? 'gudang'}_${fromDate}_to_${toDate}.xlsx`,
        )
      } catch (e) {
        console.error('Export monthly failed', e)
      }
      return
    }

    const baseQueryParams = {
      sortby: 'nameItem',
      sortdir: 'asc',
      warehouseId: id ?? null,
      from: fromDate,
      to: toDate,
      type: activeTab === 'all' ? 'current' : activeTab,
      s: searchTemp,
      itemIds:
        itemsFilter?.length > 0
          ? `[${itemsFilter.map((i) => i.value).join(',')}]`
          : undefined,
    }

    await handleDownloadExcelWithRTK(
      dataWarehouse?.name ?? '-',
      activeTab,
      data,
      setIsDownload,
      setFirstRows,
      setTotalRows,
      triggerFetchForDownload,
      baseQueryParams,
      rowsPerPage,
    )
  }

  const columns = [
    {
      name: <span>Nama Barang</span>,
      selector: 'nameItem',
      sortable: true,
      width: '300px',
      cell: (row) => <span>{row?.nameItem}</span>,
    },
    {
      name: (
        <div
          style={{
            textAlign: 'right',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {activeTab === 'in'
            ? 'Jumlah Masuk'
            : activeTab === 'out'
              ? 'Jumlah Keluar'
              : 'Jumlah'}
        </div>
      ),
      selector: 'total',
      sortable: true,
      width: '200px',
      cell: (row) => {
        return (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.total ?? 0, 2)}
              {'  '}
              {row?.unit ?? 'Kg'}
            </span>
          </div>
        )
      },
    },
    {
      name: '',
      cell: () => <></>,
    },
  ]

  const columnsMonthly = React.useMemo(
    () => [
      {
        name: <span>Jenis Barang</span>,
        selector: (row) => row?.nameItem,
        sortable: true,
        width: '300px',
        cell: (row) => <span>{row?.nameItem}</span>,
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Balance</span>
            <span id={`info-balance-m1-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-balance-m1-${id}`}
              text={`Balance pada akhir bulan sebelumnya. Digunakan sebagai saldo awal untuk perhitungan bulan ini.`}
            />
          </div>
        ),
        selector: (row) => row?.balanceMMinus1,
        sortable: true,
        right: true,
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.balanceMMinus1 ?? 0, 2)}{' '}
              {row?.unit ?? row?.item?.unit ?? ''}
            </span>
          </div>
        ),
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Barang Masuk</span>
            <span id={`info-incoming-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-incoming-${id}`}
              text={`Total jumlah barang yang masuk ke gudang selama bulan ${formattedMonthLabel}.`}
            />
          </div>
        ),
        selector: (row) => row?.incoming,
        sortable: true,
        right: true,
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.incoming ?? 0, 2)}{' '}
              {row?.unit ?? ''}
            </span>
          </div>
        ),
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Barang Keluar</span>
            <span id={`info-outgoing-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-outgoing-${id}`}
              text={`Total jumlah barang yang keluar dari gudang selama bulan ${formattedMonthLabel}.`}
            />
          </div>
        ),
        selector: (row) => row?.outgoing,
        sortable: true,
        right: true,
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.outgoing ?? 0, 2)}{' '}
              {row?.unit ?? ''}
            </span>
          </div>
        ),
      },
      {
        name: (
          <div className="d-flex align-items-center gap-1">
            <span>Balance Akhir</span>
            <span id={`info-balance-m-${id}`} style={{ cursor: 'pointer' }}>
              <Info size={14} />
            </span>
            <PMBTooltip
              target={`info-balance-m-${id}`}
              text={`Saldo pada akhir ${formattedMonthLabel} (atau balance pada tanggal berjalan jika memilih hari ini).`}
            />
          </div>
        ),
        selector: (row) => row?.balanceM,
        sortable: true,
        right: true,
        // width: '150px',
        cell: (row) => (
          <div className="d-flex justify-content-end w-100">
            <span>
              {newFormatThousandSeparator(row?.balanceM ?? 0, 2)}{' '}
              {row?.unit ?? ''}
            </span>
          </div>
        ),
      },
    ],
    [formattedMonthLabel, id],
  )

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#F9FAFB',
      },
    },
    cells: {
      style: {
        marginTop: '1px',
        marginBottom: '1px',
      },
    },
  }

  const onHandleOpenImageModal = (imageUrl) => {
    const images = Array.isArray(imageUrl) ? imageUrl : [imageUrl]
    setImagesItems(images)
    setModalOpenImage(true)
  }

  if (isLoadingById) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* BREADCRUMB */}
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs
          breadCrumbParent2="Daftar Gudang"
          breadCrumbParent2Link={'/gudang'}
          breadCrumbActive={`${dataWarehouse?.name ?? '-'}`}
        />
      </div>
      {/* END BREADCRUMB */}

      {/* PROGRESS DOWNLOAD */}
      <PMBProgressBarExportFile
        isDialogShow={isDownload}
        firstRows={firstRows}
        totalRows={totalRows}
        onCancel={() => setIsDownload(false)}
      />

      {/* ADDRESS AND PIC SECTION */}
      <div className="row">
        <div className="col-md-6 col-sm-12 mb-3">
          <Card
            className="border-0 shadow-sm"
            style={{ minHeight: MIN_MAX_HEIGHT, maxHeight: MIN_MAX_HEIGHT }}
          >
            <CardBody
              className="d-flex flex-column gap-1"
              style={{ justifyContent: 'center' }}
            >
              <div>
                <h6>Alamat</h6>
                <span className="fw-medium text-sm">
                  {dataWarehouse?.address ?? '-'}
                </span>
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6 col-sm-12 mb-3">
          <Card
            className="border-0 shadow-sm"
            style={{
              minHeight: MIN_MAX_HEIGHT,
              maxHeight: MIN_MAX_HEIGHT,
              overflow: 'auto',
            }}
          >
            <CardBody className="d-flex flex-column gap-1">
              <h6>PIC</h6>
              <div>
                <span className="fw-semibold text-sm">
                  <div className="gap-2 d-flex align-items-center">
                    {dataWarehouse?.warehousemember?.map((item) => {
                      return (
                        <>
                          <div
                            key={item?.id}
                            className="d-flex align-items-center"
                            id={`tooltip-pic-${item?.id}`}
                          >
                            <ProfilePic
                              imgSrc={item?.profilePic}
                              altText={item?.name}
                              name={item?.name}
                              avatarSeed={item?.id}
                              width={32}
                              height={32}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                          <PMBTooltip
                            placement="top"
                            target={`tooltip-pic-${item?.id}`}
                            text={item?.name}
                          />
                        </>
                      )
                    })}
                  </div>
                </span>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* MODAL IMAGE PREVIEW */}
      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={imageItems}
      />
      {/* END MODAL IMAGE PREVIEW */}

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <div className="row">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-semibold mb-0">{dataWarehouse?.name ?? '-'}</h5>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center gap-2 w-100">
                  {activeFilterCount > 0 && (
                    <CustomButton
                      onClick={handleClearFilters}
                      color="danger"
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        minWidth: '32px',
                      }}
                    >
                      <X size={16} />
                    </CustomButton>
                  )}
                  <CustomButton
                    color="primary"
                    outline
                    className="flex-grow-1 position-relative"
                    onClick={() => setIsFilterOpen((prev) => !prev)}
                    size="sm"
                    style={{
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 justify-content-center">
                      <Filter size={16} />
                      <span>Filter</span>
                      {activeFilterCount > 0 && (
                        <Badge
                          color="danger"
                          pill
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            fontSize: '10px',
                          }}
                        >
                          {activeFilterCount}
                        </Badge>
                      )}
                      {isFilterOpen ? (
                        <ChevronUp size={16} className="ml-2" />
                      ) : (
                        <ChevronDown size={16} className="ml-2" />
                      )}
                    </div>
                  </CustomButton>
                </div>

                <CustomButton
                  disabled={
                    isDownload ||
                    (activeTab === 'monthly'
                      ? monthlyLoading || !(monthlyData?.length > 0)
                      : !dataResponse?.length)
                  }
                  className="rounded w-100 w-sm-auto btn-download"
                  onClick={handleDownloadClick}
                  color="primary"
                  outline
                  size="sm"
                  style={{
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {isDownload ? (
                    <Spinner size="sm" color="primary" />
                  ) : (
                    <FileText size={16} className="me-1" />
                  )}{' '}
                  Unduh
                </CustomButton>
              </div>
            </div>

            <Collapse isOpen={isFilterOpen}>
              <div
                className="border rounded p-3 bg-light mb-3 filter-collapse"
                style={{
                  width: '100%',
                  transition: 'all 0.3s ease-in-out',
                  opacity: isFilterOpen ? 1 : 0,
                  transform: isFilterOpen
                    ? 'translateY(0)'
                    : 'translateY(-10px)',
                }}
              >
                <Row className="g-2 align-items-end">
                  <Col xs={12} md={4} lg={3}>
                    <p className="mb-2">Cari</p>
                    <InputGroup style={{ borderColor: '#D0D5DD' }}>
                      <InputGroupText
                        style={{
                          backgroundColor: 'white',
                          borderRightColor: 'white',
                        }}
                      >
                        <Search color="#667085" size={16} />
                      </InputGroupText>
                      <Input
                        placeholder="Cari nama barang..."
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                      />
                    </InputGroup>
                  </Col>

                  {activeTab !== 'all' && (
                    <Col xs={12} md="auto" lg="auto">
                      <PMBDateRangePicker
                        style={{ width: '100%', maxWidth: '100%' }}
                        value={selectedDate ?? []}
                        onChange={handleDateChange}
                        onClear={handleDateClear}
                        format="yyyy-mm-dd"
                        clearMode="callback"
                      />
                    </Col>
                  )}

                  <Col xs={12} md={4} lg={3}>
                    <PMBSelect
                      title="Jenis Barang"
                      value={itemsFilter}
                      onChange={(value) => {
                        setItemsFilter(value)
                        setCurrentPage(0)
                      }}
                      options={
                        itemsData?.items?.map((item) => ({
                          label: `${item?.nameItem || '-'}`,
                          value: item?.id,
                          image: item?.linkFoto,
                        })) || []
                      }
                      placeholder="Pilih items..."
                      isClearable
                      isMulti
                      maxMenuHeight={300}
                      menuPosition="fixed"
                      menuPlacement="bottom"
                    />
                  </Col>
                </Row>
              </div>
            </Collapse>

            {/* FILTER JENIS BARANG BADGES */}
            {itemsFilter?.length > 0 && (
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span
                  className="text-muted"
                  style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
                >
                  Filter jenis barang:
                </span>
                {itemsFilter.map((item) => (
                  <Badge
                    key={item.value}
                    color=""
                    pill
                    className="d-inline-flex align-items-center gap-1 font-medium"
                    style={{
                      backgroundColor: '#0369a1',
                      color: '#fff',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      border: '1px solid #C7D2FE',
                      lineHeight: 1.4,
                    }}
                  >
                    {item.label}
                    <button
                      type="button"
                      onClick={() => handleRemoveItemFilter(item.value)}
                      aria-label={`Hapus filter ${item.label}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#fff',
                        marginLeft: '2px',
                      }}
                    >
                      <X size={13} strokeWidth={2.5} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {/* END FILTER JENIS BARANG BADGES */}

            {/* TABLE */}
            <div>
              <DataTable
                key={`${currentPage}-${rowsPerPage}-${activeTab}`}
                customStyles={customStyles}
                columns={activeTab === 'monthly' ? columnsMonthly : columns}
                data={
                  activeTab === 'monthly' ? paginatedMonthlyData : dataResponse
                }
                progressPending={
                  activeTab === 'monthly'
                    ? monthlyLoading
                    : isLoading || isFetching
                }
                responsive
                persistTableHead
                defaultSortFieldId={1}
                defaultSortAsc={false}
                sortIcon={<ChevronDown />}
                sortServer={activeTab !== 'monthly'}
                onSort={handleSort}
                className="border p-0 border-1 rounded-top"
                progressComponent={
                  <div style={{ margin: '100px 0px' }}>
                    <Spinner />
                  </div>
                }
                noDataComponent={
                  <p className="my-3 px-3 fw-semibold">
                    Tidak ada data inventori{' '}
                    {activeTab === 'in'
                      ? 'masuk'
                      : activeTab === 'out'
                        ? 'keluar'
                        : activeTab === 'monthly'
                          ? 'untuk laporan bulanan'
                          : ''}{' '}
                    yang tersedia
                  </p>
                }
                pagination
                paginationServer={activeTab !== 'monthly'}
                paginationTotalRows={
                  activeTab === 'monthly'
                    ? monthlyData?.length || 0
                    : data?.totalItems || 0
                }
                paginationPerPage={rowsPerPage}
                paginationPageNumber={currentPage + 1}
                paginationComponent={(props) => (
                  <PMBPagination
                    rowCount={
                      activeTab === 'monthly'
                        ? monthlyData?.length || 0
                        : data?.totalItems || 0
                    }
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    setCurrentPage={setCurrentPage}
                    setRowsPerPage={setRowsPerPage}
                    props={props}
                  />
                )}
              />
            </div>
            {/* END TABLE */}

            {/* MONTHLY SUMMARY - moved to bottom of table */}
            {/* MONTHLY SUMMARY - New Card Design */}
            {activeTab === 'monthly' && (
              <div className="mt-4">
                <div className="row g-3">
                  {/* Card 1: Total Inventory */}
                  <div className="col-12 col-md-6 col-xl-3">
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-light-primary p-3 me-3 d-flex align-items-center justify-content-center"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <Package className="text-primary" size={24} />
                        </div>
                        <div>
                          <h6 className="text-muted mb-1">Total Inventory</h6>
                          <h4 className="fw-bold mb-0">
                            {monthlySummary.totalItems}
                          </h4>
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  {/* Card 2: Barang Masuk */}
                  <div className="col-12 col-md-6 col-xl-3">
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody>
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle bg-light-success p-2 me-2 d-flex align-items-center justify-content-center">
                            <ArrowDownLeft className="text-success" size={18} />
                          </div>
                          <h6 className="fw-bold mb-0">Barang Masuk</h6>
                        </div>
                        <div
                          style={{ maxHeight: '150px', overflowY: 'auto' }}
                          className="hide-scrollbar"
                        >
                          {Object.keys(monthlySummary.totalsByUnit || {})
                            .length > 0 ? (
                            Object.keys(monthlySummary.totalsByUnit || {}).map(
                              (unit) => (
                                <div
                                  key={`in-${unit}`}
                                  className="d-flex justify-content-between align-items-center mb-1 border-bottom pb-1 last-no-border"
                                >
                                  <span className="text-muted small text-uppercase">
                                    {unit}
                                  </span>
                                  <span className="fw-semibold text-success">
                                    +
                                    {newFormatThousandSeparator(
                                      monthlySummary.totalsByUnit[unit]
                                        .incoming,
                                      2,
                                    )}
                                  </span>
                                </div>
                              ),
                            )
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  {/* Card 3: Barang Keluar */}
                  <div className="col-12 col-md-6 col-xl-3">
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody>
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle bg-light-danger p-2 me-2 d-flex align-items-center justify-content-center">
                            <ArrowUpRight className="text-danger" size={18} />
                          </div>
                          <h6 className="fw-bold mb-0">Barang Keluar</h6>
                        </div>
                        <div
                          style={{ maxHeight: '150px', overflowY: 'auto' }}
                          className="hide-scrollbar"
                        >
                          {Object.keys(monthlySummary.totalsByUnit || {})
                            .length > 0 ? (
                            Object.keys(monthlySummary.totalsByUnit || {}).map(
                              (unit) => (
                                <div
                                  key={`out-${unit}`}
                                  className="d-flex justify-content-between align-items-center mb-1 border-bottom pb-1 last-no-border"
                                >
                                  <span className="text-muted small text-uppercase">
                                    {unit}
                                  </span>
                                  <span className="fw-semibold text-danger">
                                    -
                                    {newFormatThousandSeparator(
                                      monthlySummary.totalsByUnit[unit]
                                        .outgoing,
                                      2,
                                    )}
                                  </span>
                                </div>
                              ),
                            )
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  {/* Card 4: Balance Akhir */}
                  <div className="col-12 col-md-6 col-xl-3">
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody>
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle bg-light-info p-2 me-2 d-flex align-items-center justify-content-center">
                            <Activity className="text-info" size={18} />
                          </div>
                          <h6 className="fw-bold mb-0">Balance Akhir</h6>
                        </div>
                        <div
                          style={{ maxHeight: '150px', overflowY: 'auto' }}
                          className="hide-scrollbar"
                        >
                          {Object.keys(monthlySummary.totalsByUnit || {})
                            .length > 0 ? (
                            Object.keys(monthlySummary.totalsByUnit || {}).map(
                              (unit) => (
                                <div
                                  key={`bal-${unit}`}
                                  className="d-flex justify-content-between align-items-center mb-1 border-bottom pb-1 last-no-border"
                                >
                                  <span className="text-muted small text-uppercase">
                                    {unit}
                                  </span>
                                  <span className="fw-bold text-dark">
                                    {newFormatThousandSeparator(
                                      monthlySummary.totalsByUnit[unit]
                                        .balanceEnd,
                                      2,
                                    )}
                                  </span>
                                </div>
                              ),
                            )
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

export default DetailWarehouse
